import { useMemo, useState } from "react";

import { GridRowSelectionModel } from "@mui/x-data-grid";
import { emptyRowSelection, isValidDate, useConfirmationDialogContext } from "@shared";

import { AppointmentForTakeoverResponse } from "@/api";
import { useCurrentUserContext } from "@/common/contexts/current-user-context/CurrentUserContext";
import { useTranslation } from "@/translations";

import { TakeoverView } from "../Takeover.types";
import { isFromCounselor, isTakenOver, isWithinDateFilter } from "../Takeover.utils";
import { useCounselorAppointmentsQuery } from "./useCounselorAppointmentsQuery";
import { useTakeoverMutations } from "./useTakeoverMutations";
import { useTakeoverQuery } from "./useTakeoverQuery";

/**
 * Page logic for the takeover marketplace (Übernahmebörse): owns the tab switch and its per-tab
 * filters, loads the rows of the active tab, and tracks the row selection the header's bulk actions
 * work on.
 */
export function useTakeover() {
  const { t } = useTranslation();
  const { showConfirmationDialog } = useConfirmationDialogContext();
  const { currentUser } = useCurrentUserContext();
  const mutations = useTakeoverMutations();

  const [view, setViewState] = useState<TakeoverView>("toTakeOver");
  // Kept apart on purpose: "Zu übernehmen" starts at "Alle Berater:innen" and "Termine freigeben" at
  // the logged-in user, so one tab's selection must never become the other tab's starting point.
  const [takeOverCounselorId, setTakeOverCounselorId] = useState<string | null>(null);
  // Null only for a user without a counselor record of their own; they pick a colleague to start from.
  const [releaseCounselorId, setReleaseCounselorId] = useState<string | null>(currentUser.counselorId ?? null);
  const [dateFrom, setDateFromValue] = useState<Date | null>(null);
  const [dateTo, setDateToValue] = useState<Date | null>(null);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(emptyRowSelection);
  const [takeOverTarget, setTakeOverTarget] = useState<AppointmentForTakeoverResponse | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  // Loaded on both tabs: the takeover tab lists it, and the release tab still needs its count for the
  // tab badge.
  const { data: toTakeOverAppointments, isLoading: isToTakeOverLoading } = useTakeoverQuery();
  // Withholding the counselor keeps this idle while the other tab is open, so its rows are fetched on the
  // first switch rather than on every page load.
  const { data: ownAppointments, isLoading: isOwnLoading } = useCounselorAppointmentsQuery(
    view === "toRelease" ? (releaseCounselorId ?? undefined) : undefined
  );

  const appointments = useMemo(() => {
    if (view === "toTakeOver") {
      const rows = toTakeOverAppointments ?? [];
      return takeOverCounselorId === null ? rows : rows.filter((row) => isFromCounselor(row, takeOverCounselorId));
    }
    return (ownAppointments ?? []).filter((row) => isWithinDateFilter(row, dateFrom, dateTo));
  }, [view, toTakeOverAppointments, ownAppointments, takeOverCounselorId, dateFrom, dateTo]);

  // Called by every setter below: a filter change reshapes the list, so a selection made against the
  // previous one is dropped rather than left acting on rows the user can no longer see.
  const clearSelection = () => setSelectionModel(emptyRowSelection());

  const setView = (next: TakeoverView) => {
    setViewState(next);
    clearSelection();
  };

  const counselorId = view === "toTakeOver" ? takeOverCounselorId : releaseCounselorId;

  const setCounselorId = (next: string | null) => {
    // The release tab offers no "Alle Berater:innen" entry — releasing is always for exactly one.
    if (view === "toRelease" && next === null) {
      return;
    }

    if (view === "toTakeOver") {
      setTakeOverCounselorId(next);
    } else {
      setReleaseCounselorId(next);
    }
    clearSelection();
  };

  const setDateFrom = (next: Date | null) => {
    setDateFromValue(next);
    clearSelection();
  };

  const setDateTo = (next: Date | null) => {
    setDateToValue(next);
    clearSelection();
  };

  const selectedAppointments = useMemo(() => {
    if (view !== "toRelease") {
      return [];
    }
    // "Select all" arrives as type "exclude" with an empty id set, i.e. everything but nothing —
    // reading the ids as the selection would report no rows selected while all render ticked.
    const { type, ids } = selectionModel;
    return appointments.filter((appointment) =>
      type === "include" ? ids.has(appointment.appointmentId) : !ids.has(appointment.appointmentId)
    );
  }, [view, appointments, selectionModel]);
  // Marking applies to open, not-yet-marked appointments.
  const markableIds = selectedAppointments
    .filter((a) => !a.isMarkedForTakeover && !isTakenOver(a))
    .map((a) => a.appointmentId);
  // "Markierung zurücknehmen" mirrors the per-row action: unmark the still-open marked ones and
  // reverse the taken-over ones (both share that label).
  const unmarkableIds = selectedAppointments
    .filter((a) => a.isMarkedForTakeover && !isTakenOver(a))
    .map((a) => a.appointmentId);
  const reversibleIds = selectedAppointments.filter((a) => isTakenOver(a)).map((a) => a.appointmentId);

  const bulkMark = () => {
    if (markableIds.length > 0) {
      mutations.markMany.mutate(markableIds);
    }
    clearSelection();
  };

  const bulkRemove = () => {
    if (unmarkableIds.length > 0) {
      mutations.unmarkMany.mutate(unmarkableIds);
    }
    if (reversibleIds.length > 0) {
      mutations.reverseMany.mutate(reversibleIds);
    }
    clearSelection();
  };

  const mark = (row: AppointmentForTakeoverResponse) => mutations.mark.mutate(row.appointmentId);

  const unmark = (row: AppointmentForTakeoverResponse) => mutations.unmark.mutate(row.appointmentId);

  const confirmTakeOver = (appointmentId: string) =>
    mutations.takeOver.mutate(appointmentId, { onSuccess: () => setTakeOverTarget(null) });

  // Per-row reverse: confirm via the shared dialog, then re-offer the appointment.
  const requestReverse = (row: AppointmentForTakeoverResponse) =>
    showConfirmationDialog({
      dialogTitleText: t("takeover.revertDialog.title"),
      dialogText: t("takeover.revertDialog.text"),
      agreeText: t("takeover.revertDialog.confirm"),
      agreeColor: "error",
      onClose: (confirmed) => {
        if (confirmed) {
          mutations.reverse.mutate(row.appointmentId);
        }
      },
    });

  const isBusy = Object.values(mutations).some((mutation) => mutation.isPending);

  return {
    view,
    setView,
    toTakeOverCount: toTakeOverAppointments?.length ?? 0,
    counselorId,
    setCounselorId,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    appointments,
    isLoading: view === "toTakeOver" ? isToTakeOverLoading : isOwnLoading,
    isBusy,
    selectionModel,
    setSelectionModel,
    markableCount: markableIds.length,
    removableCount: unmarkableIds.length + reversibleIds.length,
    bulkMark,
    bulkRemove,
    // Identifies the visible row set, so narrowing a filter sends the table back to page 1.
    paginationResetKey: [
      view,
      counselorId ?? "",
      isValidDate(dateFrom) ? dateFrom.toISOString() : "",
      isValidDate(dateTo) ? dateTo.toISOString() : "",
    ].join("|"),
    mark,
    unmark,
    takeOverTarget,
    setTakeOverTarget,
    confirmTakeOver,
    isTakeOverPending: mutations.takeOver.isPending,
    requestReverse,
    isHistoryOpen,
    openHistory: () => setIsHistoryOpen(true),
    closeHistory: () => setIsHistoryOpen(false),
  };
}
